import { getTranslate } from '../../localization'
import { HealthStatus } from '../../types/inventoryHealth'

/**
 * Human text for each machine finding code.
 *
 * The server deliberately returns codes rather than sentences so the wording
 * lives here and can be translated. Each entry answers the operator's real
 * question — "what do I do about this?" — rather than restating the code.
 */
const FINDING_TEXT: Record<string, string> = {
  PMS_MISSING:
    'The PMS holds nothing for this date, but a downstream layer does. The stale value is still being sold.',
  CRS_MISSING:
    'The PMS has a value that never reached the CRS. The pipeline stopped between them.',
  CHANNEL_MISSING:
    'The CRS has a value that never reached this channel. Check the channel is subscribed and its credentials work.',
  RATE_MISMATCH_PMS_CRS:
    'The PMS and CRS disagree on price. Neither layer has a channel dimension, so this is a genuine pipeline fault.',
  RATE_MISMATCH_CRS_CHANNEL:
    'The CRS and this channel disagree on price, after applying the subscription rate multiplier. The channel is selling a number the property did not set.',
  RATE_MISMATCH_PMS_CHANNEL:
    'The PMS and this channel disagree on price. The price reaches the channel from the PMS directly for this property, so this is the end-to-end check and a genuine fault.',
  ALLOCATION_MISMATCH_CRS_CHANNEL:
    'The CRS and this channel disagree on how many rooms are allocated.',
  OCCUPANCY_SET_MISMATCH:
    'The layers price different occupancies. One of them is missing a rate the other has.',
  RATE_ABSENT_EVERYWHERE:
    'This date exists in the pipeline but no layer holds a price for it.',
  STALE_PMS:
    'The PMS value has not changed for longer than the freshness threshold.',
  STALE_CRS:
    'The CRS value has not changed for longer than the freshness threshold.',
  STALE_CHANNEL:
    'This channel value has not changed for longer than the freshness threshold.',
  UNSUBSCRIBED_DATA:
    'This channel holds inventory for a date and rate plan nobody subscribed it to. The data exists but was never asked for.',
  CHANNEL_RATES_DIVERGE:
    'Channels hold different prices from one another for the same date and rate plan. This provider prices per agency credential, so each channel receives its own price — a comparison made with a different credential will return different numbers by design.',
  CRS_NOT_PRICE_BEARING:
    'The CRS holds no price anywhere in this window while the PMS and the channels do. The price reaches each channel from the PMS directly, so there is no single house rate for the CRS to carry and a zero there is expected rather than missing data. Rates are checked PMS-to-channel for this property.',
  CRS_RATE_MANUALLY_SET:
    'This CRS rate was set by hand, so it differs from the PMS by intent rather than by fault. The override is written through to the CRS layer, so the rate shown here is the rate that sells. An override that no longer matches the CRS rate, or that covers only some occupancies, is still reported as a mismatch.'
}

export function findingText(code: string): string {
  return getTranslate(FINDING_TEXT[code] ?? code)
}

/**
 * Short label for the row-level chip. Falls back to the raw code so a finding
 * added server-side still renders something rather than disappearing.
 */
export function findingLabel(code: string): string {
  return getTranslate(code.replace(/_/g, ' ').toLowerCase())
}

/**
 * Text colours for a severity, resolved per theme.
 *
 * The light values were chosen against white: Material's #ed6c02 measures about
 * 3.1:1 there and fails WCAG 1.4.3 AA for body text, so the warning tone is
 * darkened. That reasoning had no dark-mode branch, and these are applied as
 * inline styles, so the theme could not override them — on a dark Paper the
 * same four values fall to roughly 1.7:1 to 2.6:1, with the warning tone (the
 * one darkened to fix light mode) the worst of them. Every verdict line and
 * every assessment cell on this screen is one of these four colours, so that
 * was the screen's primary output rendered unreadable in dark mode.
 *
 * Colour is never the only carrier of meaning here — each coloured element sits
 * beside text saying the same thing — but a verdict an operator cannot read is
 * no better than one that is not shown.
 */
const LIGHT: Record<string, string> = {
  Critical: '#c62828',
  Warning: '#a04100',
  Normal: '#2e7d32',
  default: '#616161'
}

// Material's 200/300 tints, which are the intended pairing for dark surfaces.
const DARK: Record<string, string> = {
  Critical: '#ef9a9a',
  Warning: '#ffb74d',
  Normal: '#a5d6a7',
  default: '#bdbdbd'
}

export function statusColor(status: HealthStatus, dark = false): string {
  const palette = dark ? DARK : LIGHT

  return palette[status] ?? palette.default
}

/**
 * The colour for the verdict line.
 *
 * An empty window is never green. The server may report `Normal` for a window
 * it found nothing in, and pairing that with "nothing was compared — this is
 * not a clean result" would make the colour contradict the sentence, which is
 * exactly the false reassurance this screen exists to remove.
 */
export function verdictColor(
  status: HealthStatus,
  totalRows: number,
  dark = false
): string {
  if (totalRows === 0) {
    return statusColor('Warning', dark)
  }

  return statusColor(status, dark)
}

/**
 * The one-line verdict shown above the table.
 *
 * Deliberately distinguishes "no data" from "healthy". A window with zero rows
 * means nothing was found to compare, which is not the same as three layers
 * agreeing, and reporting it green would be the exact false reassurance this
 * screen exists to remove.
 */
export function verdictText(
  status: HealthStatus,
  totalRows: number,
  differingRows: number
): string {
  if (totalRows === 0) {
    return getTranslate(
      'No inventory found for this property, provider and date range. Nothing was compared — this is not a clean result.'
    )
  }

  if (differingRows === 0) {
    return getTranslate(
      'All {total} comparisons agree across the PMS, CRS and channel layers.',
      { total: totalRows }
    )
  }

  if (status === 'Critical') {
    return getTranslate(
      '{differing} of {total} comparisons disagree between layers. The data is not healthy.',
      { differing: differingRows, total: totalRows }
    )
  }

  return getTranslate(
    '{differing} of {total} comparisons carry a warning. Values agree; some are older than the freshness threshold.',
    { differing: differingRows, total: totalRows }
  )
}

/**
 * The three parts of a rendered finding: what it is, how many rows, and what to
 * do about it.
 *
 * Extracted so the COMPOSITION is testable. The defect this fixes lived exactly
 * here — the panel rendered `detail || text`, and since the server always sets
 * detail, the explanation was unreachable. Both `findingLabel` and `findingText`
 * passed their own tests throughout; nothing exercised the two together.
 */
export function findingParts(finding: { code: string; detail: string }): {
  label: string
  detail: string
  explanation: string
} {
  return {
    label: findingLabel(finding.code),
    detail: finding.detail,
    explanation: findingText(finding.code)
  }
}

/**
 * A key that stays unique even if the server stops aggregating findings per
 * code. `subject` is already carried on the type and was unused; two findings
 * sharing a code with different subjects would collide as React keys.
 */
export function findingKey(finding: {
  code: string
  subject?: string
}): string {
  return finding.subject ? `${finding.code}-${finding.subject}` : finding.code
}
